import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ConfirmableActionService } from '@peca/backoffice/services/confirmable-action.service';
import { AsyncState } from '@peca/backoffice/models/async-state.model';
import { Pageable } from '@peca/backoffice/models/pageable.model';
import { ToastService } from '@peca/commons';
import { AutocompleteItem, AutocompleteItemSource, BreadcrumbItem, Pagination, PaginationInitialState } from '@peca/design-system';
import { BehaviorSubject } from 'rxjs';

import { RegionModalFormComponent } from './region-modal-form/region-modal-form.component';
import { RegionsService } from '../../services/regions.service';
import { RegionQuery } from '../../types/region-query.type';
import { Region, RegionResponse } from '../../types/region.type';

@Component({
  selector: 'peca-regions',
  templateUrl: './regions.component.html',
  styleUrls: ['./regions.component.scss'],
  standalone: false,
})
export class RegionsComponent implements OnInit {
  @ViewChild('regionModal') regionModal!: RegionModalFormComponent;

  private readonly service = inject(RegionsService);
  private readonly toast = inject(ToastService);
  private readonly confirmable = inject(ConfirmableActionService);

  readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Operações', path: '/operacoes' },
    { label: 'Regiões', path: '/operacoes/regioes' },
  ];

  regionsState: AsyncState<Pageable<Region>> = { loading: true };
  readonly pagination$ = new BehaviorSubject<Pagination>({ ...PaginationInitialState, page: 1, size: 12 });
  readonly suggestions$ = new BehaviorSubject<AutocompleteItemSource>({});

  readonly searchForm = new FormGroup({
    search: new FormControl(''),
    startZipCode: new FormControl(''),
    endZipCode: new FormControl(''),
  });

  lastQuery: RegionQuery = {
    page: 0,
    size: 12,
    sort: 'audit.createdAt,desc',
  };

  ngOnInit() {
    this.fetch(this.lastQuery);
  }

  fetch(query: RegionQuery) {
    this.lastQuery = query;
    this.regionsState = { loading: true };

    this.service.list(query).subscribe({
      next: (data) => {
        this.paginate(data);
        this.regionsState = {
          data: {
            ...data,
            items: data.items.map(this.mapToRegion),
          },
        };
      },
      error: () => (this.regionsState = { failure: true }),
    });
  }

  onPaginate(pagination: Pagination) {
    const page = pagination.page ? pagination.page - 1 : 0;

    this.fetch({
      ...this.lastQuery,
      page,
      size: pagination.size,
    });
  }

  onSubmitSearch() {
    const raw = this.searchForm.getRawValue();

    this.fetch({
      ...this.lastQuery,
      page: 0,
      search: raw.search?.trim() || undefined,
      startZipCode: raw.startZipCode ? Number(raw.startZipCode.replace(/\D/g, '')) : undefined,
      endZipCode: raw.endZipCode ? Number(raw.endZipCode.replace(/\D/g, '')) : undefined,
    });
  }

  clearFilters() {
    this.searchForm.reset({
      search: '',
      startZipCode: '',
      endZipCode: '',
    });

    this.fetch({
      ...this.lastQuery,
      page: 0,
      search: undefined,
      startZipCode: undefined,
      endZipCode: undefined,
    });
  }

  onClickCreateRegion() {
    this.regionModal.openCreate();
  }

  onClickDetails(region: Region) {
    this.regionModal.openDetails(region.id);
  }

  onRegionCreated() {
    this.fetch({ ...this.lastQuery, page: 0 });
  }

  onClickDelete(region: Region) {
    this.confirmable.confirm('Você está removendo uma região.').subscribe(({ confirmed }) => {
      if (!confirmed) return;

      this.service.delete(region.id).subscribe({
        next: () => {
          this.toast.success('Região removida com sucesso.');
          this.fetch(this.lastQuery);
        },
        error: () => this.toast.failure('Não foi possível remover a região.'),
      });
    });
  }

  private paginate(page: Pageable<unknown>) {
    this.pagination$.next({
      page: page.currentPage + 1,
      size: page.pageSize,
      total: page.totalPages,
      items: page.totalItems,
    });
  }

  private mapToRegion(region: RegionResponse): Region {
    return {
      id: region.id,
      description: region.name,
      originZipCode: region.originZipCode,
      destinationZipCode: region.destinationZipCode,
      audit: region.audit,
    };
  }
}
